import React from "react";
import { motion } from "framer-motion";
import { useSiteContent } from "@/contexts/SiteContentContext";

const COLORS = ["#FF5B17", "#FF2D9C", "#5B2BFF", "#00E5A0", "#FF5B17", "#0A0A0A"];

export default function Services() {
  const { content } = useSiteContent();
  const items = content.services.items.map((it, i) => ({
    n: String(i + 1).padStart(2, "0"),
    title: it.title,
    body: it.body,
    note: it.note,
    color: COLORS[i % COLORS.length],
  }));
  return (
    <section
      id="services"
      data-testid="services-section"
      className="band bg-[#FFD500] text-[#0A0A0A] py-20 lg:py-28"
    >
      <div className="max-w-[1500px] mx-auto px-5 lg:px-8 relative">
        {/* Massive backdrop number */}
        <div
          aria-hidden="true"
          className="absolute right-2 lg:right-8 top-6 font-display select-none pointer-events-none"
          style={{
            fontSize: "clamp(10rem, 32vw, 36rem)",
            lineHeight: 0.78,
            color: "rgba(10,10,10,0.06)",
          }}
        >
          06
        </div>

        {/* Tape banner — inline above header */}
        <div className="mb-6">
          <div className="wiggle inline-block" style={{ "--r": "-3deg" }}>
            <div className="sticker">
              ③ Services
            </div>
          </div>
        </div>

        <div className="relative grid grid-cols-12 gap-6 items-end border-b-2 border-[#0A0A0A] pb-6">
          <div className="col-span-12 lg:col-span-8">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid="services-title"
              className="font-display"
              style={{
                fontSize: "clamp(2.75rem, 9vw, 8rem)",
                lineHeight: 0.82,
                letterSpacing: "-0.02em",
              }}
            >
              Six things<br />
              we do <span className="font-hand text-[#FF2D9C]" style={{letterSpacing:0}}>obsessively</span> well.
            </motion.h2>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pb-3">
            <p className="font-mono text-sm leading-relaxed">
              {content.services.intro}
            </p>
          </div>
        </div>

        {/* Services as tilted cards grid */}
        <div className="relative mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {items.map((s, i) => (
            <motion.article
              key={s.n}
              initial={{ opacity: 0, y: 24, rotate: 0 }}
              whileInView={{ opacity: 1, y: 0, rotate: (i % 3 === 0 ? -1.5 : i % 3 === 1 ? 1 : -0.5) }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              whileHover={{ rotate: 0, y: -6 }}
              className="relative bg-[#FAF5E8] border-2 border-[#0A0A0A] rounded-xl p-6 lg:p-7 group"
              style={{ boxShadow: "6px 6px 0 #0A0A0A" }}
              data-testid={`service-${s.n}`}
            >
              <div
                className="absolute -top-3 -left-3 w-12 h-12 rounded-full flex items-center justify-center font-display text-xl border-2 border-[#0A0A0A]"
                style={{ background: s.color, color: s.color === "#0A0A0A" ? "#FAF5E8" : "#0A0A0A" }}
              >
                {s.n}
              </div>
              <h3 className="font-display text-3xl lg:text-4xl mt-3 leading-none">
                {s.title}
              </h3>
              <p className="mt-4 font-mono text-[13px] leading-relaxed text-[#0A0A0A]/80">
                {s.body}
              </p>
              <div
                className="mt-5 font-hand text-2xl"
                style={{ color: s.color === "#0A0A0A" ? "#FF2D9C" : s.color }}
              >
                {s.note}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
